import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { login as apiLogin, logout as apiLogout, me } from "@/api/auth";
import { setUnauthorizedHandler } from "@/lib/api";
import type { AdminDto } from "@/types/api";

interface AuthContextValue {
    admin: AdminDto | null;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<AdminDto>;
    logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactNode {
    const queryClient = useQueryClient();

    const meQuery = useQuery({
        queryKey: ["auth", "me"],
        queryFn: me,
        retry: false,
        staleTime: 5 * 60 * 1000,
    });

    // If any API call 401s, clear our cached auth.
    React.useEffect(() => {
        setUnauthorizedHandler(() => {
            queryClient.setQueryData(["auth", "me"], null);
        });
    }, [queryClient]);

    const loginMut = useMutation({
        mutationFn: ({ email, password }: { email: string; password: string }) => apiLogin(email, password),
        onSuccess: (admin) => {
            queryClient.setQueryData(["auth", "me"], admin);
        },
    });

    const logoutMut = useMutation({
        mutationFn: apiLogout,
        onSettled: () => {
            queryClient.setQueryData(["auth", "me"], null);
            queryClient.clear();
        },
    });

    const value: AuthContextValue = React.useMemo(
        () => ({
            admin: meQuery.data ?? null,
            isLoading: meQuery.isLoading,
            login: async (email, password) => loginMut.mutateAsync({ email, password }),
            logout: async () => {
                await logoutMut.mutateAsync();
            },
        }),
        [meQuery.data, meQuery.isLoading, loginMut, logoutMut]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = React.useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
    return ctx;
}
